import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import WorkspaceLayout from '../components/Workspace/WorkspaceLayout';

interface CriticalFunction {
  id: string;
  name: string;
  department: string;
  owner: string;
  rto: string;
  lastTested: string;
  status: 'Critical' | 'At risk' | 'Ready' | 'Untested';
}

const CRITICAL_FUNCTIONS: CriticalFunction[] = [
  {
    id: '1',
    name: 'Payroll processing',
    department: 'Finance',
    owner: 'A. Okafor',
    rto: '24h',
    lastTested: 'Never',
    status: 'Critical',
  },
  {
    id: '2',
    name: 'Customer support line',
    department: 'Operations',
    owner: 'J. Tremblay',
    rto: '4h',
    lastTested: '5 months ago',
    status: 'At risk',
  },
  {
    id: '3',
    name: 'Order fulfilment',
    department: 'Logistics',
    owner: 'M. Singh',
    rto: '8h',
    lastTested: '12 days ago',
    status: 'Ready',
  },
  {
    id: '4',
    name: 'Patient records access',
    department: 'Clinical IT',
    owner: 'R. Bello',
    rto: '1h',
    lastTested: '2 months ago',
    status: 'Ready',
  },
  {
    id: '5',
    name: 'Vendor payments',
    department: 'Finance',
    owner: 'A. Okafor',
    rto: '48h',
    lastTested: 'Never',
    status: 'Untested',
  },
  {
    id: '6',
    name: 'Email & collaboration',
    department: 'IT',
    owner: 'K. Lavoie',
    rto: '2h',
    lastTested: '3 weeks ago',
    status: 'Ready',
  },
];

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeFunctionTab, setActiveFunctionTab] = useState<'all' | 'gaps' | 'untested'>('all');
  const [dismissedAgentDraft, setDismissedAgentDraft] = useState(false);

  const filteredFunctions = CRITICAL_FUNCTIONS.filter((fn) => {
    if (activeFunctionTab === 'gaps') {
      return fn.status === 'Critical' || fn.status === 'At risk';
    }
    if (activeFunctionTab === 'untested') {
      return fn.status === 'Untested' || fn.lastTested === 'Never';
    }
    return true;
  });

  const getStatusBadge = (status: CriticalFunction['status']) => {
    switch (status) {
      case 'Critical':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEE2E2] text-[#991B1B]">
            Critical
          </span>
        );
      case 'At risk':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEF3C7] text-[#B45309]">
            At risk
          </span>
        );
      case 'Ready':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#DCFCE7] text-[#166534]">
            Ready
          </span>
        );
      case 'Untested':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">
            Untested
          </span>
        );
    }
  };

  return (
    <WorkspaceLayout
      title="Resilience overview"
      subtitle="Plan v3.2 · last updated 2 days ago by Planning agent"
      onExportReport={() => window.print()}
      onNewExercise={() => navigate('/tabletop-exercises')}
    >
      <div className="space-y-6">
        {/* Top 4 Metrics Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 1. Resilience Score */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Resilience Score
            </div>
            <div className="mt-2 flex items-baseline space-x-2">
              <span className="text-3xl font-extrabold text-slate-900">72</span>
              <span className="text-xs font-semibold text-emerald-600">▲ 6 this quarter</span>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#047857] rounded-full" style={{ width: '72%' }}></div>
            </div>
          </div>

          {/* 2. Plan Coverage */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Plan Coverage
            </div>
            <div className="mt-2">
              <span className="text-3xl font-extrabold text-slate-900">18</span>
              <span className="text-xl font-bold text-slate-400">/22</span>
            </div>
            <div className="mt-2 text-xs text-slate-500 font-medium">
              critical functions have a playbook
            </div>
          </div>

          {/* 3. Last Exercise */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Last Exercise
            </div>
            <div className="mt-2">
              <span className="text-3xl font-extrabold text-slate-900">B+</span>
            </div>
            <div className="mt-2 text-xs text-slate-500 font-medium truncate">
              Regional power outage · 12 days ago
            </div>
          </div>

          {/* 4. Open Actions */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Open Actions
            </div>
            <div className="mt-2 flex items-baseline space-x-2">
              <span className="text-3xl font-extrabold text-slate-900">14</span>
              <span className="text-xs font-bold text-[#DC2626]">3 overdue</span>
            </div>
            <div className="mt-2 text-xs text-slate-500 font-medium">
              across 6 owners
            </div>
          </div>
        </div>

        {/* Two Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Critical Functions (7 cols) */}
          <div className="lg:col-span-8 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col justify-between">
            <div>
              {/* Header & Tabs */}
              <div className="p-6 pb-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
                <h2 className="text-base font-bold text-slate-900">Critical functions</h2>

                <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded-lg text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setActiveFunctionTab('all')}
                    className={`px-3 py-1 rounded-md transition-colors ${
                      activeFunctionTab === 'all'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveFunctionTab('gaps')}
                    className={`px-3 py-1 rounded-md transition-colors ${
                      activeFunctionTab === 'gaps'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Gaps
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveFunctionTab('untested')}
                    className={`px-3 py-1 rounded-md transition-colors ${
                      activeFunctionTab === 'untested'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Untested
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      <th className="py-3 px-6">Function</th>
                      <th className="py-3 px-4">Owner</th>
                      <th className="py-3 px-4">RTO</th>
                      <th className="py-3 px-4">Last Tested</th>
                      <th className="py-3 px-6 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredFunctions.map((fn) => (
                      <tr
                        key={fn.id}
                        className="hover:bg-slate-50/70 transition-colors cursor-pointer"
                        onClick={() => navigate('/impact-analysis')}
                      >
                        <td className="py-3.5 px-6">
                          <div className="font-semibold text-slate-900">{fn.name}</div>
                          <div className="text-[11px] text-slate-400">{fn.department}</div>
                        </td>
                        <td className="py-3.5 px-4 font-medium text-slate-700">
                          {fn.owner}
                        </td>
                        <td className="py-3.5 px-4 font-bold text-slate-900">
                          {fn.rto}
                        </td>
                        <td className="py-3.5 px-4 text-slate-500 font-normal">
                          {fn.lastTested}
                        </td>
                        <td className="py-3.5 px-6 text-right">
                          {getStatusBadge(fn.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer link */}
            <div className="p-4 px-6 border-t border-slate-100 bg-slate-50/40">
              <Link
                to="/impact-analysis"
                className="text-xs font-bold text-indigo-900 hover:text-indigo-700 inline-flex items-center space-x-1"
              >
                <span>View all 22 functions</span>
                <span>→</span>
              </Link>
            </div>
          </div>

          {/* Right Column: Upcoming Exercises & Agent Activity (4 cols) */}
          <div className="lg:col-span-4 space-y-6">
            {/* Upcoming Exercises */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h2 className="text-base font-bold text-slate-900 mb-4">Upcoming exercises</h2>

              <div className="space-y-3">
                {/* Exercise 1 */}
                <div className="flex items-center space-x-3.5 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors">
                  <div className="w-12 h-12 rounded-lg border border-slate-200 overflow-hidden flex flex-col items-center flex-shrink-0 text-center">
                    <div className="w-full bg-[#EF4444] text-white text-[9px] font-bold py-0.5 tracking-wider uppercase">
                      Oct
                    </div>
                    <div className="text-sm font-extrabold text-slate-900 pt-0.5">14</div>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900">
                      Ransomware at month-end
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      8 participants · 90 min
                    </div>
                  </div>
                </div>

                {/* Exercise 2 */}
                <div className="flex items-center space-x-3.5 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors">
                  <div className="w-12 h-12 rounded-lg border border-slate-200 overflow-hidden flex flex-col items-center flex-shrink-0 text-center">
                    <div className="w-full bg-[#3B82F6] text-white text-[9px] font-bold py-0.5 tracking-wider uppercase">
                      Nov
                    </div>
                    <div className="text-sm font-extrabold text-slate-900 pt-0.5">03</div>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900">
                      Key supplier insolvency
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      5 participants · 60 min
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Activity */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-slate-900">Agent activity</h2>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#DCFCE7] text-[#166534]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A]"></span>
                  3 running
                </span>
              </div>

              <div className="space-y-4 text-xs">
                {/* Agent Item 1 */}
                {!dismissedAgentDraft && (
                  <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg space-y-2.5">
                    <div className="flex items-start space-x-2.5">
                      <div className="w-6 h-6 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                        PL
                      </div>
                      <div className="text-slate-800 leading-snug">
                        <strong className="font-semibold">Planning agent</strong> drafted a
                        recovery playbook for Payroll processing.
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 pl-8.5">
                      <button
                        type="button"
                        onClick={() => navigate('/impact-analysis')}
                        className="px-2.5 py-1 bg-[#0F172A] hover:bg-slate-800 text-white rounded text-[11px] font-semibold transition-colors"
                      >
                        Review draft
                      </button>
                      <button
                        type="button"
                        onClick={() => setDismissedAgentDraft(true)}
                        className="px-2.5 py-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-600 rounded text-[11px] font-semibold transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}

                {/* Agent Item 2 */}
                <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg space-y-1">
                  <div className="flex items-start space-x-2.5">
                    <div className="w-6 h-6 rounded-md bg-rose-100 text-rose-700 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                      RK
                    </div>
                    <div className="text-slate-800 leading-snug">
                      <strong className="font-semibold">Risk agent</strong> flagged a
                      single-vendor dependency in Customer support.
                    </div>
                  </div>
                  <div className="pl-8.5 text-[10px] text-slate-500">
                    Source: Vendor_Contracts_2025.pdf, p. 14
                  </div>
                </div>

                {/* Agent Item 3 */}
                <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                  <div className="flex items-start space-x-2.5">
                    <div className="w-6 h-6 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                      EX
                    </div>
                    <div className="text-slate-800 leading-snug">
                      <strong className="font-semibold">Exercise agent</strong> prepared 11
                      injects for Oct 14 from your IT incident plan.
                    </div>
                  </div>
                </div>
              </div>

              {/* View all agents link */}
              <div className="mt-4 pt-3 border-t border-slate-100 text-center">
                <Link
                  to="/agents"
                  className="text-xs font-semibold text-indigo-900 hover:text-indigo-700"
                >
                  Manage all agents →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};

export default Dashboard;